// Network warm-up at script start (iteration 20, narrowed after review): hints for the shadow
// stylesheet(s) the configured widgets will link, and `<link rel="preconnect">` to the API /
// image origins a widget that draws on this page will hit. boot() runs both before the DOM wait,
// after prepareSearch() (it needs to know whether results draws now), only for a booted config
// with widgets (never on an experiment's null arm — no widget runs there, so nothing would use
// them); both plan from the booted config's own widgets.
//
// WHY: each SDK starts these only when its widget is created or draws (search / the assistant
// add their own stylesheet hints inside create(), rce when it renders; every API connection
// opens on the first request) — i.e. after DOMContentLoaded and after the widgets before it.
// widget.js may run while the parser is still in <head>, so warming up here overlaps the DNS /
// TCP / TLS handshakes and the stylesheet download with the rest of the page's parsing.
//
// Stylesheet rules (must request EXACTLY what the widget's shadow-root `<link rel=stylesheet>`
// will, or the browser fetches twice):
//   - one URL per distinct stylesheet: `runtime.stylesUrl`, or `runtime.stylesUrlNoProduct` for a
//     widget whose config sets `disableDefaultProductCardStyles` (or with `runtime.bare`) — the
//     SDKs' own getStylesUrl rule; none for a widget with `disableDefaultStyles`; nothing when
//     the URL is undefined (the SDK then falls back to paths the facade doesn't know);
//   - `rel=preload as=style` ONLY for a widget that renders right after DOM readiness: results
//     when prepareSearch() decided it draws (`drawsNow.results`). A preload is fetched at the
//     highest priority (competing with the shop's own render-blocking CSS) and Chrome warns
//     "preloaded but not used" when nothing links it within a few seconds — which is what the
//     click widgets (linked on first open) and carousels (linked on a draw that may never come)
//     would do on most page views;
//   - `rel=prefetch` (idle priority, no unused warning, kept in the HTTP cache) for every other
//     configured widget's URL not already preloaded: the click widgets, the assistant modal, a
//     results widget that doesn't draw now, the carousels. The SDKs' own create-time prefetch
//     of the same URL (search dropdown / modal, assistant) then finds it cached — it doesn't make
//     ours redundant: ours starts at script start instead of after DOMContentLoaded, and rce
//     has no hint of its own;
//   - NO `crossorigin` on either: the shadow-root link fetches without CORS, and a hinted
//     response is reused only when the request modes match (sdk-search results/widget.ts
//     `preloadStyles`, same reason);
//   - `nonce` = the first `cspNonce` of a configured widget config (the SDKs set the same on
//     their own links);
//   - skipped when the document already has a preload (or, for a prefetch, a prefetch) <link>
//     with that href.
//
// Preconnect rules — only origins a widget that DRAWS will hit soon (an open-time request of a
// click widget — the dropdown's suggest, the chatbot — is left to the visitor's click; a hinted
// connection idles out unused otherwise). A connection is only reused by a request in the SAME
// pool — credentialed vs anonymous — so the attribute must match how the client fetches:
//   - search API (`searchBaseApiUrl`, `__REGION__` = the region of the results config's apiKey)
//     when results draws now: the generated search-api-client fetches CORS without credentials
//     (no `credentials` set → fetch's default "same-origin" = none cross-origin) → `crossorigin`
//     (anonymous);
//   - rce API (`rceBaseApiUrl`, region of the carousels' rceConfig.apiKey) when a carousel is
//     configured: rce's plain fetch() with default credentials → anonymous;
//   - image CDN (`imageBaseUrl`) when results draws now or a carousel is configured: plain
//     `<img>` (credentialed no-cors) → NO crossorigin.
//   Click-only pages get none. At most MAX_PRECONNECTS origins, in that priority order,
//   de-duplicated (origin + pool), never the page's own origin, never one an existing
//   `link[rel=preconnect]` already covers. An apiKey that isn't a valid key (3 region letters +
//   hex — sdk-common `regionFromKey`'s rule, copied: this module stays import-free) leaves a
//   `__REGION__` URL out: that widget fails its own key check anyway.
//
// `rel` values are matched in JS (token list, case-insensitive), not with the CSS4 ` i`
// attribute-selector flag, which throws in older engines.
//
// Pure planning (stylesheetsToWarm / originsToPreconnect) + DOM helpers, import-free (types
// only), so it unit-tests under plain Node (test/warmup.test.ts).

import type { RaventicSdkConfig } from "./config";
import type { RuntimeUrls } from "./runtime";

export const MAX_PRECONNECTS = 3;

const API_KEY_PATTERN = /^[a-z]{3}[a-f0-9]+$/;
const REGION_TOKEN = "__REGION__";

// The widget-config fields this module reads (every SDK config has them, optional).
interface StyleFields {
  disableDefaultStyles?: boolean;
  disableDefaultProductCardStyles?: boolean;
  cspNonce?: string;
}

// What renders right after DOM readiness on this page view (from prepareSearch()'s plan).
export interface DrawsNow {
  results: boolean;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export interface StylesheetWarmup {
  // `rel=preload as=style`: linked right after DOM readiness.
  preload: string[];
  // `rel=prefetch`: linked later, maybe (on open / on a draw).
  prefetch: string[];
  nonce?: string;
}

// The distinct shadow stylesheet URLs the configured widgets will link, split by how soon (see
// the header).
export function stylesheetsToWarm(cfg: RaventicSdkConfig, runtime: RuntimeUrls, drawsNow: DrawsNow): StylesheetWarmup {
  const preload: string[] = [];
  const prefetch: string[] = [];
  let nonce: string | undefined;
  const carousels = Array.isArray(cfg.rce?.carousels) ? (cfg.rce!.carousels as unknown[]) : [];
  const widgets: Array<{ config: unknown; now: boolean }> = [
    { config: cfg.search?.results?.config, now: drawsNow.results },
    { config: cfg.search?.dropdown?.config, now: false },
    { config: cfg.search?.modal?.config, now: false },
    { config: cfg.assistant?.modal?.config, now: false },
    ...carousels.map((c) => ({ config: isObject(c) ? c.widgetConfig : undefined, now: false })),
  ];
  for (const { config, now } of widgets) {
    if (!isObject(config)) continue;
    const fields = config as StyleFields;
    if (nonce === undefined && typeof fields.cspNonce === "string" && fields.cspNonce !== "") nonce = fields.cspNonce;
    if (fields.disableDefaultStyles === true) continue;
    const bare = fields.disableDefaultProductCardStyles === true || runtime.bare === true;
    const url = bare ? runtime.stylesUrlNoProduct : runtime.stylesUrl;
    if (!url) continue;
    if (now) {
      if (!preload.includes(url)) preload.push(url);
    } else if (!prefetch.includes(url)) {
      prefetch.push(url);
    }
  }
  const warm: StylesheetWarmup = { preload, prefetch: prefetch.filter((url) => !preload.includes(url)) };
  if (nonce !== undefined) warm.nonce = nonce;
  return warm;
}

export interface PreconnectTarget {
  origin: string;
  // true → `crossorigin` (anonymous pool: the API clients' CORS fetches without credentials);
  // false → none (credentialed pool: plain <img>).
  anonymous: boolean;
}

// `base` with `__REGION__` replaced by `apiKey`'s region → its origin; null when it can't be
// resolved (no URL, a region token without a valid key, an unparsable URL).
function regionOrigin(base: string | undefined, apiKey: unknown): string | null {
  if (!base) return null;
  let url = base;
  if (url.includes(REGION_TOKEN)) {
    if (typeof apiKey !== "string" || !API_KEY_PATTERN.test(apiKey)) return null;
    url = url.replace(REGION_TOKEN, apiKey.substring(0, 3));
  }
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

// The origins to preconnect to, in priority order, capped (see the header). `pageOrigin` =
// `location.origin` (skipped: the page's own connection already exists).
export function originsToPreconnect(
  cfg: RaventicSdkConfig,
  runtime: RuntimeUrls,
  pageOrigin: string,
  drawsNow: DrawsNow,
): PreconnectTarget[] {
  const resultsConfig = cfg.search?.results?.config as unknown;
  const results = drawsNow.results && isObject(resultsConfig) ? resultsConfig : null;
  const carousels = Array.isArray(cfg.rce?.carousels) ? (cfg.rce!.carousels as unknown[]).filter(isObject) : [];

  const candidates: Array<{ origin: string | null; anonymous: boolean }> = [];
  if (results) candidates.push({ origin: regionOrigin(runtime.searchBaseApiUrl, results.apiKey), anonymous: true });
  if (carousels.length > 0) {
    const key = carousels.map((c) => (isObject(c.rceConfig) ? c.rceConfig.apiKey : undefined)).find((k) => typeof k === "string");
    candidates.push({ origin: regionOrigin(runtime.rceBaseApiUrl, key), anonymous: true });
  }
  if (results || carousels.length > 0) {
    candidates.push({ origin: regionOrigin(runtime.imageBaseUrl, undefined), anonymous: false });
  }

  const out: PreconnectTarget[] = [];
  for (const { origin, anonymous } of candidates) {
    if (origin === null || origin === "null" || origin === pageOrigin) continue;
    if (out.some((t) => t.origin === origin && t.anonymous === anonymous)) continue;
    out.push({ origin, anonymous });
    if (out.length === MAX_PRECONNECTS) break;
  }
  return out;
}

// The document's `<link>`s whose `rel` holds the `rel` token (case-insensitively, like the
// browser's own matching) — filtered in JS, see the header.
function linksWithRel(doc: Document, rel: string): Array<{ href: string | null; crossOrigin: string | null }> {
  return Array.from(doc.querySelectorAll<HTMLLinkElement>("link[rel]"))
    .filter((link) => (link.getAttribute("rel") ?? "").toLowerCase().split(/\s+/).includes(rel))
    .map((link) => ({ href: link.getAttribute("href"), crossOrigin: link.getAttribute("crossorigin") }));
}

const head = (doc: Document): Element => doc.head ?? doc.getElementsByTagName("head")[0]!;

// Appends the stylesheet hints: `<link rel="preload" as="style">` per preload URL and
// `<link rel="prefetch">` per prefetch URL, each unless the document already has one. Returns
// how many it appended.
export function warmStylesheets({ preload, prefetch, nonce }: StylesheetWarmup, doc: Document = document): number {
  if (preload.length === 0 && prefetch.length === 0) return 0;
  const preloaded = linksWithRel(doc, "preload").map((l) => l.href);
  const prefetched = linksWithRel(doc, "prefetch").map((l) => l.href);
  let added = 0;
  const append = (rel: "preload" | "prefetch", url: string): void => {
    const link = doc.createElement("link");
    link.rel = rel;
    if (rel === "preload") link.as = "style";
    link.href = url;
    if (nonce !== undefined) link.nonce = nonce;
    head(doc).appendChild(link);
    added++;
  };
  for (const url of preload) if (!preloaded.includes(url)) append("preload", url);
  for (const url of prefetch) if (!preloaded.includes(url) && !prefetched.includes(url)) append("prefetch", url);
  return added;
}

// Appends one `<link rel="preconnect">` per target not already covered by an existing one
// (same origin, same pool). Returns how many it appended.
export function preconnect(targets: readonly PreconnectTarget[], doc: Document = document): number {
  if (targets.length === 0) return 0;
  const existing = linksWithRel(doc, "preconnect").map(({ href, crossOrigin }) => {
    let origin: string | null = null;
    try {
      origin = href === null ? null : new URL(href).origin;
    } catch {
      // A relative / broken href covers nothing we'd add.
    }
    return { origin, anonymous: crossOrigin !== null && crossOrigin.toLowerCase() !== "use-credentials" };
  });
  let added = 0;
  for (const { origin, anonymous } of targets) {
    if (existing.some((e) => e.origin === origin && e.anonymous === anonymous)) continue;
    const link = doc.createElement("link");
    link.rel = "preconnect";
    link.href = origin;
    if (anonymous) link.crossOrigin = "anonymous";
    head(doc).appendChild(link);
    added++;
  }
  return added;
}
